/**
 * Converts a string to a URL-friendly format
 * @param str - The string to convert
 * @returns URL-friendly string (lowercase, spaces replaced with underscores, special chars removed)
 */
export function toUrlFriendly(str: string): string {
  return str
    .toLowerCase()
    .trim()
    // Polish character transliteration
    .replace(/ą/g, 'a')
    .replace(/ć/g, 'c')
    .replace(/ę/g, 'e')
    .replace(/ł/g, 'l')
    .replace(/ń/g, 'n')
    .replace(/ó/g, 'o')
    .replace(/ś/g, 's')
    .replace(/ź/g, 'z')
    .replace(/ż/g, 'z')
    // Remove special characters except spaces, letters, and underscores
    // (underscores must stay so applying this twice is idempotent — e.g. login resume
    // must not turn `linas_music_studio` into `linasmusicstudio`).
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
}

/**
 * Generates a URL-friendly identifier for a space
 * @param spaceName - The name of the space
 * @param city - The city where the space is located
 * @returns URL-friendly string in format "city/space_name"
 */
export function generateSpaceUrlId(spaceName: string, city: string): string {
  const urlFriendlyCity = toUrlFriendly(city);
  const urlFriendlySpaceName = toUrlFriendly(spaceName);
  return `${urlFriendlyCity}/${urlFriendlySpaceName}`;
}

/**
 * Extracts city and space name from a URL-friendly identifier
 * @param urlId - The URL-friendly identifier (e.g., "warszawa/sanctum_sound")
 * @returns Object with city and spaceName
 */
export function parseSpaceUrlId(urlId: string): { city: string; spaceName: string } | null {
  const parts = urlId.split('/');
  if (parts.length !== 2) {
    return null;
  }
  
  return {
    city: parts[0].replace(/_/g, ' '),
    spaceName: parts[1].replace(/_/g, ' ')
  };
}

/**
 * Generates a URL-friendly identifier for a room
 * @param roomName - The name of the room
 * @returns URL-friendly string
 */
export function generateRoomUrlId(roomName: string): string {
  return toUrlFriendly(roomName);
}

/** True when two values map to the same public URL slug, including collapsed underscores. */
export function urlFriendlyEquals(left: string, right: string): boolean {
  const a = toUrlFriendly(left);
  const b = toUrlFriendly(right);
  return a === b || a.replace(/_/g, '') === b.replace(/_/g, '');
}

/**
 * Extracts room name from a URL-friendly identifier
 * @param urlId - The URL-friendly identifier (e.g., "studio_a")
 * @returns Room name
 */
export function parseRoomUrlId(urlId: string): string {
  return urlId.replace(/_/g, ' ');
}

/**
 * Converts a URL-friendly city name back to its proper case
 * @param urlCityName - URL-friendly city name (e.g., "warszawa", "olsztyn")
 * @param availableCities - List of available cities with proper capitalization
 * @returns Properly capitalized city name, or the original if not found
 */
export function fromUrlFriendlyCity(urlCityName: string, availableCities: string[]): string {
  if (!urlCityName) {
    return '';
  }

  // Known cities fallback (for when API hasn't loaded yet)
  const knownCities: Record<string, string> = {
    'warszawa': 'Warszawa',
    'olsztyn': 'Olsztyn',
  };

  // Normalize URL city name: replace underscores with spaces and convert to lowercase
  const normalizedUrlCity = urlCityName.replace(/_/g, ' ').toLowerCase();

  // First, try to find in known cities fallback
  if (knownCities[normalizedUrlCity]) {
    return knownCities[normalizedUrlCity];
  }

  // If we have available cities list, try to match
  if (availableCities && availableCities.length > 0) {
    // Try to find matching city in the list (case-insensitive)
    const matchingCity = availableCities.find(
      city => toUrlFriendly(city).toLowerCase() === normalizedUrlCity
    );

    if (matchingCity) {
      return matchingCity;
    }

    // If no match found, try simple case-insensitive comparison
    const simpleMatch = availableCities.find(
      city => city.toLowerCase() === normalizedUrlCity
    );

    if (simpleMatch) {
      return simpleMatch;
    }
  }

  // Fallback: capitalize first letter of each word
  return urlCityName
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
